import { needsAssets } from '../content/needsPresentation';

const pyramidCache = new Map<string, Promise<string>>();

const rasterizePyramid = async (source: string) => {
  const response = await fetch(source);
  if (!response.ok) {
    throw new Error('Unable to load the Needs pyramid for PDF export');
  }
  const svg = new DOMParser().parseFromString(await response.text(), 'image/svg+xml');
  // html2canvas skips these SVGs without intrinsic dimensions. Rasterize a
  // temporary copy at high resolution; keep the original asset untouched.
  const width = 2272;
  const height = 1600;
  svg.documentElement.setAttribute('width', String(width));
  svg.documentElement.setAttribute('height', String(height));
  const url = URL.createObjectURL(
    new Blob([new XMLSerializer().serializeToString(svg)], { type: 'image/svg+xml' })
  );
  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('Unable to render the Needs pyramid'));
      image.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Unable to prepare the Needs pyramid for PDF export');
    }
    context.fillStyle = '#FFFFFF';
    context.fillRect(0, 0, width, height);
    // The paired Needs template scales its image containers by 1.1. A 5%
    // inset keeps the outer labels inside the exported page after that scale.
    context.drawImage(image, width * 0.05, height * 0.05, width * 0.9, height * 0.9);
    return canvas.toDataURL('image/png');
  } finally {
    URL.revokeObjectURL(url);
  }
};

const getPdfPyramid = (source: string) => {
  let prepared = pyramidCache.get(source);
  if (!prepared) {
    prepared = rasterizePyramid(source).catch(error => {
      pyramidCache.delete(source);
      throw error;
    });
    pyramidCache.set(source, prepared);
  }
  return prepared;
};

const withNeedsPyramid = async (result: any) => {
  const source = needsAssets[result?.favorite?.key]?.pyramid;
  if (!source) {
    return result;
  }
  return {
    ...result,
    favorite: { ...result.favorite, imgPyramid: await getPdfPyramid(source) },
  };
};

export const prepareNeedsPdfResult = async (kind: 'needs' | 'needsPro', result: any) => {
  if (kind === 'needsPro') {
    return withNeedsPyramid(result);
  }
  const [pro, perso] = await Promise.all([
    withNeedsPyramid(result.pro),
    withNeedsPyramid(result.perso),
  ]);
  return { ...result, pro, perso };
};
